import type { EventLine, EventQuestion, MdsEvent } from './types';

/** The two lines that read the same for every event held here. */
const HOUSE_NOTES: EventLine[] = [
  ['Parking', 'Free on-site parking for all attendees'],
  ['Language', 'Arabic and English'],
];

/** Good to know, with only the two per-event lines spelled out. */
const goodToKnow = (who: string, bring: string): EventLine[] => [
  ['Who it is for', who],
  ['Bring', bring],
  ...HOUSE_NOTES,
];

/**
 * The three questions every registration asks. An event's own `questions` are
 * appended to these by the form, so a new event that needs nothing extra ships
 * with an empty array rather than a copy of this list.
 */
export const BASE_QUESTIONS: EventQuestion[] = [
  { id: 'what-you-do', label: 'What do you do?', type: 'text', required: false },
  {
    id: 'heard-via',
    label: 'How did you hear about this?',
    type: 'select',
    required: false,
    options: ['Instagram', 'Facebook', 'A friend', 'Walked past', 'University', 'Other'],
  },
  { id: 'mailing-list', label: 'Join the mailing list?', type: 'checkbox', required: false },
];

/**
 * The autumn 2026 programme, in date order.
 *
 * `iso` is the only date on a row. "Thu 17 Sep", the month heading the index
 * groups under and the day badge on the card are all derived from it by
 * `@/lib/format` — the design carried `date`, `month` and `iso` side by side,
 * and by the time it had twelve events two of them disagreed.
 *
 * `seats` and `taken` are likewise the only counts. `seatsLeft` is a
 * subtraction, and a subtraction does not need storing.
 */
export const EVENTS: MdsEvent[] = [
  {
    slug: 'open-house-space-tour',
    title: 'Open House & Space Tour',
    iso: '2026-09-17',
    time: '6:00–8:00 PM',
    space: 'coworking-desks',
    location: 'Coworking floor',
    kind: 'Open house',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'No ticket needed. Registering helps us plan the coffee.',
    blurb:
      'Walk the floor, meet the team and see every space before you book. Free coffee from the café.',
    description:
      'An open evening for anyone curious about MDS. Walk the coworking floor, look inside the meeting rooms and the studio, and ask whatever you like about rates, opening hours and how booking works. The team is on the floor the whole evening.',
    seats: 60,
    taken: 41,
    agenda: [
      ['6:00 PM', 'Doors open: coffee and introductions in the café'],
      ['6:30 PM', 'Guided walk of the desks, rooms and studio'],
      ['7:15 PM', 'Packages, student rates and questions'],
      ['8:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Freelancers, students and teams considering a desk',
      'Nothing, just an ID if you want to open an account',
    ),
    questions: [
      { id: 'interest', label: 'Which space brings you in?', type: 'text', required: false },
    ],
  },
  {
    slug: 'founders-night',
    title: 'Founders Night at the Café',
    iso: '2026-09-26',
    time: '4:00–7:00 PM',
    space: 'cafe-hall',
    location: 'Café Hall · 60 seats',
    kind: 'Networking',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free entry. Café orders are paid at the counter.',
    blurb: 'Short talks from local founders, then open networking over the tables.',
    description:
      'Four short talks from founders working out of Minieh and Tripoli, then the tables are yours. Come to compare notes on hiring, first customers and the hard first year, and stay for the part that matters most, which is the conversation afterwards.',
    seats: 60,
    taken: 52,
    agenda: [
      ['4:00 PM', 'Arrivals and seating'],
      ['4:30 PM', 'Four founder talks, ten minutes each'],
      ['5:30 PM', 'Open networking'],
      ['7:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Founders, early teams and anyone thinking of starting',
      'Cards if you hand them out',
    ),
    questions: [
      { id: 'dietary', label: 'Dietary needs', type: 'text', required: false },
      { id: 'table-of', label: 'Coming as a group? How many?', type: 'text', required: false },
    ],
  },
  {
    slug: 'digital-skills-workshop',
    title: 'Digital Skills Workshop',
    iso: '2026-10-07',
    time: '5:00–9:00 PM',
    space: 'meeting-training-room',
    location: 'Training Room · 20 seats',
    kind: 'Workshop',
    priceMinor: 1500,
    currency: 'USD',
    priceNote: 'Paid at the front desk. Worksheets and a certificate included.',
    blurb: 'Hands-on laptop session. Bring your own machine.',
    description:
      'A practical, laptop-open session on the tools a small business actually runs on: shared drives, spreadsheets that hold up, invoicing, and a social presence that takes minutes a week rather than hours. You leave with the files you built on the night.',
    seats: 20,
    taken: 13,
    agenda: [
      ['5:00 PM', 'Setup and skills check'],
      ['5:30 PM', 'Spreadsheets and shared drives'],
      ['7:00 PM', 'Invoicing and social presence'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Small business owners and anyone running their own admin',
      'A laptop and its charger',
    ),
    questions: [
      {
        id: 'level',
        label: 'Experience level',
        type: 'select',
        required: true,
        options: ['Beginner', 'Some spreadsheets', 'Confident'],
      },
      {
        id: 'student-rate',
        label: 'Applying for the student rate?',
        type: 'checkbox',
        required: false,
      },
    ],
  },
  {
    slug: 'podcast-recording-clinic',
    title: 'Podcast Recording Clinic',
    iso: '2026-10-16',
    time: '6:00–9:00 PM',
    space: 'creative-studio',
    location: 'Creative Studio · 8 seats',
    kind: 'Studio clinic',
    priceMinor: 2000,
    currency: 'USD',
    priceNote: 'Studio time on the night is included. Paid at the desk.',
    blurb: 'Learn the studio setup: mics, levels and editing basics, then record a test episode.',
    description:
      'A working session in the creative studio. We cover the desk, the microphones and the levels, then everyone records a short test episode and listens back to it. Editing basics at the end, with the project files to take away.',
    seats: 8,
    taken: 6,
    agenda: [
      ['6:00 PM', 'Studio tour and equipment walkthrough'],
      ['6:45 PM', 'Mic technique and levels'],
      ['7:45 PM', 'Record a test episode'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Anyone recording audio: podcasters, teachers, marketers',
      'Headphones and a topic you can talk about for five minutes',
    ),
    questions: [
      { id: 'topic', label: 'What would you record?', type: 'textarea', required: false },
    ],
  },
  {
    slug: 'freelancers-roundtable',
    title: 'Freelancers Roundtable',
    iso: '2026-10-27',
    time: '7:00–9:00 PM',
    space: 'small-meeting-room',
    location: 'Small Meeting Room · 6 seats',
    kind: 'Roundtable',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free, but the seat is held. Cancel if you cannot make it.',
    blurb: 'A small-group session on pricing, contracts and finding clients from Minieh.',
    description:
      'Six seats around one table. We work through what people are actually stuck on: what to charge, what belongs in a contract, and where the next client comes from when the referrals dry up. Nobody presents; everybody talks.',
    seats: 6,
    taken: 4,
    agenda: [
      ['7:00 PM', 'Introductions and problem round'],
      ['7:30 PM', 'Pricing and contracts'],
      ['8:30 PM', 'Finding clients'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Working freelancers, at any stage',
      'A pricing question you have not solved',
    ),
    questions: [
      {
        id: 'stuck-on',
        label: 'What are you stuck on?',
        type: 'textarea',
        required: true,
      },
    ],
  },
  {
    slug: 'community-coworking-day',
    title: 'Community Coworking Day',
    iso: '2026-11-07',
    time: '10:00 AM–4:00 PM',
    space: 'coworking-desks',
    location: 'Coworking floor',
    kind: 'Open day',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free all day. Desks are first come, first served.',
    blurb: 'Desks open to everyone for the day. Bring a project.',
    description:
      'The floor is open to everyone for the day, at no charge. Take a desk, bring whatever you are working on, and use the café when you need a break. There is an optional tour in the afternoon for anyone who wants to see the rest of the building.',
    seats: 40,
    taken: 22,
    agenda: [
      ['10:00 AM', 'Floor opens: pick any desk'],
      ['12:30 PM', 'Café lunch service'],
      ['2:00 PM', 'Optional space tour'],
      ['4:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Anyone with a project and a laptop',
      'Your own machine (power and wifi are here)',
    ),
    questions: [],
  },
  {
    slug: 'cv-portfolio-clinic',
    title: 'CV & Portfolio Clinic',
    iso: '2026-11-18',
    time: '5:00–8:00 PM',
    space: 'meeting-training-room',
    location: 'Training Room · 20 seats',
    kind: 'Workshop',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free. Bring a printed copy if you have one.',
    blurb: 'Bring the CV you are sending out and leave with one worth sending.',
    description:
      'Two hours on the page itself: what a recruiter reads first, what to cut, and how to describe work nobody paid you for. The last hour is one-to-one: three reviewers on the floor, fifteen minutes each, on whatever you brought.',
    seats: 20,
    taken: 9,
    agenda: [
      ['5:00 PM', 'What gets read, and in what order'],
      ['6:00 PM', 'Portfolios for people without a portfolio'],
      ['7:00 PM', 'One-to-one reviews'],
      ['8:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Graduates and anyone applying for work this year',
      'Your current CV, on a laptop or on paper',
    ),
    questions: [
      { id: 'field', label: 'What field are you applying in?', type: 'text', required: false },
      {
        id: 'review-slot',
        label: 'Want a one-to-one review slot?',
        type: 'checkbox',
        required: false,
      },
    ],
  },
  {
    slug: 'women-in-tech-meetup',
    title: 'Women in Tech Meetup',
    iso: '2026-11-26',
    time: '6:00–9:00 PM',
    space: 'cafe-hall',
    location: 'Café Hall · 60 seats',
    kind: 'Networking',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free entry. Café orders are paid at the counter.',
    blurb: 'Three speakers from Tripoli and Beirut, then the room is open.',
    description:
      'An evening built around the part that is hard to find locally: people further along the same road. Three speakers on how they got where they are, then open tables until nine. The café counter is staffed throughout.',
    seats: 60,
    taken: 31,
    agenda: [
      ['6:00 PM', 'Arrivals and coffee'],
      ['6:30 PM', 'Three talks, fifteen minutes each'],
      ['7:30 PM', 'Open tables'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Women working in or moving into tech, at any stage',
      'Nothing, come as you are',
    ),
    questions: [{ id: 'dietary', label: 'Dietary needs', type: 'text', required: false }],
  },
  {
    slug: 'kids-code-saturday',
    title: 'Kids Code Saturday',
    iso: '2026-12-05',
    time: '10:00 AM–1:00 PM',
    space: 'meeting-training-room',
    location: 'Training Room · 20 seats',
    kind: 'Workshop',
    priceMinor: 1000,
    currency: 'USD',
    priceNote: 'Per child. Machines provided, no laptop needed.',
    blurb: 'A first morning of code for ages 9–13. Machines provided.',
    description:
      'Block-based to start, typed code by the end of the morning, and a small game each child takes home a link to. Two instructors on twenty seats. Parents are welcome to stay in the café or sit at the back.',
    seats: 20,
    taken: 15,
    agenda: [
      ['10:00 AM', 'Machines out, first sprite moving'],
      ['11:00 AM', 'Loops, and the first typed line'],
      ['12:00 PM', 'Build your own small game'],
      ['1:00 PM', 'Show and close'],
    ],
    goodToKnow: goodToKnow(
      'Children aged 9 to 13, with a parent on site',
      'Nothing, the machines are here',
    ),
    questions: [
      { id: 'child-name', label: "Child's name", type: 'text', required: true },
      { id: 'child-age', label: "Child's age", type: 'text', required: true },
      {
        id: 'parent-staying',
        label: 'Will a parent stay on site?',
        type: 'checkbox',
        required: true,
      },
    ],
  },
  {
    slug: 'year-end-demo-night',
    title: 'Year-End Demo Night',
    iso: '2026-12-12',
    time: '6:00–9:00 PM',
    space: 'cafe-hall',
    location: 'Café Hall · 60 seats',
    kind: 'Networking',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free. Tell us when you register if you want a demo slot.',
    blurb: 'Everything built at MDS this year, shown by the people who built it.',
    description:
      'Ten demos, five minutes each, from desk holders and workshop alumni: finished things, half-finished things and one or two that did not work. Open tables after, and the floor stays open late.',
    seats: 60,
    taken: 38,
    agenda: [
      ['6:00 PM', 'Doors and coffee'],
      ['6:30 PM', 'Ten demos, five minutes each'],
      ['7:30 PM', 'Open tables'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Anyone who has used the building this year, and anyone curious',
      'Your laptop if you are demoing',
    ),
    questions: [
      { id: 'demo-slot', label: 'Want a demo slot?', type: 'checkbox', required: false },
      { id: 'demo-what', label: 'What would you demo?', type: 'textarea', required: false },
    ],
  },
  {
    slug: 'grant-writing-roundtable',
    title: 'Grant Writing Roundtable',
    iso: '2026-12-17',
    time: '7:00–9:00 PM',
    space: 'small-meeting-room',
    location: 'Small Meeting Room · 6 seats',
    kind: 'Roundtable',
    priceMinor: 0,
    currency: 'USD',
    priceNote: 'Free, but the seat is held. Cancel if you cannot make it.',
    blurb: 'Six seats, one table, and the applications everyone is halfway through.',
    description:
      'For people writing to funders rather than customers. Bring the application you are stuck in the middle of; we read each other’s budget lines, argue about the theory of change, and leave with the next paragraph written.',
    seats: 6,
    taken: 3,
    agenda: [
      ['7:00 PM', 'What everyone is applying for'],
      ['7:30 PM', 'Budgets and the lines funders query'],
      ['8:15 PM', 'Read and mark up each other’s drafts'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'NGOs, cooperatives and anyone applying for funding',
      'The draft you are working on',
    ),
    questions: [
      { id: 'applying-to', label: 'Who are you applying to?', type: 'text', required: false },
    ],
  },
  {
    slug: 'studio-open-session',
    title: 'Studio Open Session',
    iso: '2026-12-22',
    time: '5:00–9:00 PM',
    space: 'creative-studio',
    location: 'Creative Studio · 8 seats',
    kind: 'Studio clinic',
    priceMinor: 2000,
    currency: 'USD',
    priceNote: 'Includes an operator and the files on the night.',
    blurb: 'Half-hour recording slots with an operator on the desk. Eight slots, booked in order.',
    description:
      'The studio is open with someone on the desk for the whole evening. Book a half-hour slot, come in, record whatever you came to record, and walk out with the files. No teaching this one. It is bench time with a hand on the controls.',
    seats: 8,
    taken: 2,
    agenda: [
      ['5:00 PM', 'First slot'],
      ['7:00 PM', 'Break (café is open)'],
      ['7:30 PM', 'Remaining slots'],
      ['9:00 PM', 'Close'],
    ],
    goodToKnow: goodToKnow(
      'Anyone with something ready to record',
      'Your script, and headphones if you prefer your own',
    ),
    questions: [
      {
        id: 'slot',
        label: 'Preferred slot',
        type: 'select',
        required: true,
        options: ['5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:30 PM', '8:00 PM', '8:30 PM'],
      },
      { id: 'recording', label: 'What are you recording?', type: 'text', required: false },
    ],
  },
];

/** The category filter on `/events`, derived from the rows rather than declared. */
export const EVENT_KINDS: string[] = [...new Set(EVENTS.map((e) => e.kind))];

/** Seats still available. The one number the card leads with, and never stored. */
export const seatsLeft = (event: MdsEvent): number => Math.max(0, event.seats - event.taken);

/** Look an event up by slug. */
export const getEvent = (slug: string): MdsEvent | undefined => EVENTS.find((e) => e.slug === slug);

/**
 * Events on or after `from`, oldest first — what the index and the home strip
 * show. `from` is passed in rather than read off the clock here so a page can
 * be rendered against a fixed date in a test.
 */
export const upcomingEvents = (from: Date = new Date()): MdsEvent[] => {
  const floor = from.toISOString().slice(0, 10);
  return EVENTS.filter((e) => e.iso >= floor);
};

/** Everything except `slug`, for the "Also coming up" rail. */
export const otherEvents = (slug: string, limit = 3): MdsEvent[] =>
  EVENTS.filter((e) => e.slug !== slug).slice(0, limit);
